import React, { useState, useEffect, useCallback } from "react";
import {
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell, TableCellLayout,
  Button, Input, Switch, Dropdown, Option, Label, Field, Spinner, Textarea,
} from "@fluentui/react-components";
import { Delete20Regular, Add20Filled, Edit20Regular, Save20Regular, Dismiss20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import { getAccessToken } from "../../Services/GraphServices";
import { getSharedMailTemplates, saveMailTemplate, deleteMailTemplate } from "../../Services/HeedifyServices";
import { useNotification } from "../GenericComponent/NotificationSystem";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import "./MailFlowsComponent.css";

/**
 * Gabarits de mail PARTAGÉS.
 *
 * ─── Pourquoi cet écran ne montre que les partagés ──────────────────────────────────────────
 * Le backend distingue depuis toujours deux natures de gabarit : le PERSONNEL, que tout agent
 * crée pour lui-même, et le PARTAGÉ, réservé aux administrateurs du scope parce qu'il parle au
 * nom de l'entreprise. Le personnel est un brouillon de travail — un administrateur n'a pas à le
 * lire, encore moins à le corriger. Cet écran administre donc le partagé, et la console
 * administrera le personnel. Le service applique la même coupure de son côté (`mode=admin`).
 *
 * ─── Le corps édité est le corps BRUT ───────────────────────────────────────────────────────
 * La lecture ordinaire de l'API rend du HTML déjà substitué : `{{agentName}}` y est remplacé par
 * un vrai nom. L'éditer et le réenregistrer figerait CE nom dans le gabarit pour tout le monde.
 * D'où `mode=admin`, qui rend le brut — avec ses marqueurs intacts.
 *
 * ─── Les trois natures ne servent pas au même endroit ───────────────────────────────────────
 *   REPLY      inséré par l'agent dans sa réponse, et VU par lui avant l'envoi.
 *   SIGNATURE  identifie l'agent ; il la voit et peut écrire en dessous.
 *   AUTO_ACK   part SEUL, sans relecture, sur l'accusé de réception d'un flux mail. C'est le seul
 *              qui échappe au regard d'un humain — d'où l'avertissement affiché à la saisie.
 */

const KINDS = ["REPLY", "SIGNATURE", "AUTO_ACK"];

const emptyTemplate = (scope) => ({
  kind: "REPLY",
  scope: scope || "default",
  title: "",
  subject: "",
  body: "",
  language: "fr",
  shortcut: "",
  enabled: true,
});

const MailTemplatesComponent = ({ ACTIVE_TENANT, SCOPES_NAMES }) => {
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const { showSuccess, showError } = useNotification();

  const [scope, setScope] = useState("default");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    if (!ACTIVE_TENANT) return;
    setLoading(true);
    try {
      const token = await getAccessToken(instance, activeAccount);
      setTemplates(await getSharedMailTemplates(ACTIVE_TENANT, scope, token));
    } catch (e) {
      showError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [ACTIVE_TENANT, scope, instance, activeAccount, showError]);

  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    if (!String(editing.title || "").trim()) { showError(t("MAIL_TEMPLATE_TITLE_REQUIRED")); return; }
    if (!String(editing.body || "").trim()) { showError(t("MAIL_TEMPLATE_BODY_REQUIRED")); return; }
    setSaving(true);
    try {
      const token = await getAccessToken(instance, activeAccount);
      const { _rid, _self, _etag, _attachments, _ts, ...payload } = editing;
      const result = await saveMailTemplate(ACTIVE_TENANT, payload, token);
      if (!result.success) { showError(result.message); return; }
      showSuccess(t("MAIL_TEMPLATE_SAVED"));
      setEditing(null);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (tpl) => {
    const token = await getAccessToken(instance, activeAccount);
    const result = await deleteMailTemplate(ACTIVE_TENANT, tpl.id, token);
    if (!result.success) { showError(result.message); return; }
    showSuccess(t("MAIL_TEMPLATE_DELETED"));
    setToDelete(null);
    await load();
  };

  const patch = (changes) => setEditing((tpl) => ({ ...tpl, ...changes }));

  /* ── Liste ──────────────────────────────────────────────────────────────────────────── */
  if (!editing) {
    return (
      <div className="mailflows-container">
        <div className="mailflows-header">
          <h2>{t("MAIL_TEMPLATES")}</h2>
          <div className="mailflows-header-actions">
            {Array.isArray(SCOPES_NAMES) && SCOPES_NAMES.length > 0 && (
              <Dropdown
                value={scope}
                selectedOptions={[scope]}
                onOptionSelect={(_, d) => setScope(d.optionValue)}
                style={{ minWidth: 180 }}
              >
                {SCOPES_NAMES.map((s) => <Option key={s} value={s}>{s}</Option>)}
              </Dropdown>
            )}
            <Button appearance="primary" icon={<Add20Filled />} onClick={() => setEditing(emptyTemplate(scope))}>
              {t("ADD")}
            </Button>
          </div>
        </div>

        <p className="mailflows-hint">{t("MAIL_TEMPLATE_SHARED_ONLY")}</p>

        {loading ? <Spinner label={t("LOADING")} /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t("NAME")}</TableHeaderCell>
                <TableHeaderCell>{t("TEMPLATE_KIND")}</TableHeaderCell>
                <TableHeaderCell>{t("SUBJECT")}</TableHeaderCell>
                <TableHeaderCell>{t("SHORTCUT")}</TableHeaderCell>
                <TableHeaderCell>{t("LANGUAGE")}</TableHeaderCell>
                <TableHeaderCell>{t("ENABLED")}</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((tpl) => (
                <TableRow key={tpl.id}>
                  <TableCell><TableCellLayout>{tpl.title}</TableCellLayout></TableCell>
                  <TableCell><TableCellLayout>{t(`TEMPLATE_KIND_${tpl.kind}`)}</TableCellLayout></TableCell>
                  <TableCell><TableCellLayout>{tpl.subject || "—"}</TableCellLayout></TableCell>
                  <TableCell><TableCellLayout>{tpl.shortcut || "—"}</TableCellLayout></TableCell>
                  <TableCell><TableCellLayout>{tpl.language || "—"}</TableCellLayout></TableCell>
                  <TableCell><TableCellLayout>{tpl.enabled === false ? t("NO") : t("YES")}</TableCellLayout></TableCell>
                  <TableCell>
                    <Button appearance="subtle" icon={<Edit20Regular />} onClick={() => setEditing(tpl)} />
                    <Button appearance="subtle" icon={<Delete20Regular />} onClick={() => setToDelete(tpl)} />
                  </TableCell>
                </TableRow>
              ))}
              {templates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}><TableCellLayout>{t("NO_DATA_AVAILABLE")}</TableCellLayout></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        <ConfirmAlert
          open={!!toDelete}
          name={toDelete?.title}
          onConfirm={() => onDelete(toDelete)}
          onCancel={() => setToDelete(null)}
        />
      </div>
    );
  }

  /* ── Édition ────────────────────────────────────────────────────────────────────────── */
  return (
    <div className="mailflows-container">
      <div className="mailflows-header">
        <h2>{editing.id ? t("EDIT") : t("ADD")} — {t("MAIL_TEMPLATES")}</h2>
        <div className="mailflows-header-actions">
          <Button icon={<Dismiss20Regular />} onClick={() => setEditing(null)}>{t("CANCEL")}</Button>
          <Button appearance="primary" icon={<Save20Regular />} disabled={saving} onClick={onSave}>
            {saving ? <Spinner size="tiny" /> : t("SAVE")}
          </Button>
        </div>
      </div>

      <div className="mailflows-form">
        <div className="mailflows-inline">
          <Field label={t("NAME")} required>
            <Input value={editing.title || ""} onChange={(_, d) => patch({ title: d.value })} />
          </Field>
          <Field label={t("TEMPLATE_KIND")} hint={t(`TEMPLATE_KIND_${editing.kind}_HINT`)}>
            <Dropdown
              value={t(`TEMPLATE_KIND_${editing.kind}`)}
              selectedOptions={[editing.kind]}
              onOptionSelect={(_, d) => patch({ kind: d.optionValue })}
            >
              {KINDS.map((k) => <Option key={k} value={k}>{t(`TEMPLATE_KIND_${k}`)}</Option>)}
            </Dropdown>
          </Field>
          <Field label={t("LANGUAGE")}>
            <Input value={editing.language || ""} onChange={(_, d) => patch({ language: d.value })} />
          </Field>
          <Field label={t("SHORTCUT")} hint={t("MAIL_TEMPLATE_SHORTCUT_HINT")}>
            <Input value={editing.shortcut || ""} onChange={(_, d) => patch({ shortcut: d.value })} />
          </Field>
        </div>

        {/* AUTO_ACK part sans qu'aucun humain ne le relise : c'est le seul cas où une faute de
            frappe atteint le client sans filtre. L'avertissement n'apparaît que là. */}
        {editing.kind === "AUTO_ACK" && (
          <div className="mailflows-warning">{t("TEMPLATE_KIND_AUTO_ACK_WARNING")}</div>
        )}

        {/* Un objet n'a de sens que pour un mail qui en porte un. Une signature s'insère dans un
            corps existant, elle n'en a pas. */}
        {editing.kind !== "SIGNATURE" && (
          <Field label={t("SUBJECT")}>
            <Input value={editing.subject || ""} onChange={(_, d) => patch({ subject: d.value })} />
          </Field>
        )}

        <Field label={t("BODY")} required hint={t("MAIL_TEMPLATE_BODY_HINT")}>
          <Textarea
            rows={12}
            value={editing.body || ""}
            onChange={(_, d) => patch({ body: d.value })}
            className="mailflows-mono"
          />
        </Field>

        {/* Les marqueurs disponibles, écrits noir sur blanc. Une clef inconnue rend une chaîne
            VIDE côté serveur — jamais le marqueur — donc une faute de frappe ne se voit pas dans
            le mail reçu : elle se voit ici, ou nulle part. */}
        <div className="mailflows-rules">
          <Label weight="semibold">{t("AVAILABLE_VARIABLES")}</Label>
          <p className="mailflows-hint">{t("MAIL_TEMPLATE_VARIABLES_HINT")}</p>
          <code className="mailflows-code">
            {"{{customerName}} · {{greetingName}} · {{caseRef}} · {{subject}} · {{agentName}} · {{agentEmail}} · {{mailbox}} · {{queueName}}"}
          </code>
        </div>

        <div className="mailflows-switch">
          <Label>{t("ENABLED")}</Label>
          <Switch checked={editing.enabled !== false} onChange={(_, d) => patch({ enabled: d.checked })} />
        </div>
      </div>
    </div>
  );
};

export default MailTemplatesComponent;
